<template>
  <div class="max-w-[480px] relative">
    <!-- Navbar -->
    <BlNavBarSearchMv
      :value="keywords"
      :has-right-button="isLoggedIn"
      placeholder="Cari kelas atau penyedia"
      class="sticky z-40 bg-white top-0 border-b border-b-[#EBEBEB]"
      has-back-button
      @handleBackButtonClick="onCloseWebview"
      @handleClickRightButton="goToTransaksiPage"
      @input="handleSearch"
    >
      <IcoEticket slot="hasRightButtonIcon" />
    </BlNavBarSearchMv>
    <!-- End of Navbar -->

    <FilterSortTab
      data-testid="landing-page-filter-tab"
      @leftTabClick="$router.push({name: 'search-location'})"
      @middleTabClick="$router.push({name: 'search-filter'})"
      @rightTabClick="$router.push({name: 'search-sort'})"
    />

    <!-- Main Banner -->
    <div class="edu-karier-main-banner-wrapper">
      <BlSliderMv
        v-if="getBanners[MAIN_BANNER_CATEGORY].length"
        :slider-item-margin="0"
        :slider-item-radius="0"
        :slider-item-height="216"
        :slider-item-width="fullScreenWidth"
        :items="getBanners[MAIN_BANNER_CATEGORY]"
        class="edu-karier-main-banner"
        @itemChange="onBannerChange(...arguments, {category: MAIN_BANNER_CATEGORY})"
        @click="trackBannerOnClick(...arguments, {category: MAIN_BANNER_CATEGORY})"
      />
    </div>
    <!-- End of Main Banner -->

    <!-- Announcement -->
    <div class="relative">
      <BlAnnouncementMv
        v-if="getAnnouncements.list.length"
        :content="getAnnouncements.list"
        :is-show-close-button="getAnnouncements.isCloseable"
        is-close-per-content
      />
    </div>
    <!-- End of Announcement -->

    <!-- Green Box -->
    <BestCourses />
    <!-- End of Green Box -->

    <CategoryAndMerchantTab />

    <DynamicSection category="edu-karier-landing-primary" />
    <div class="w-full h-3 bg-[#f2f2f2] mt-16" />

    <MerchantRegistrationBanner />
    <div class="w-full h-3 bg-[#f2f2f2]" />

    <PageArticle />

    <div v-if="isLoggedIn" class="qa-onboard-ticket absolute top-0 right-0 p-16" style="width: 56px; height: 56px">
      <IcoEticket />
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {BlNavBarSearchMv, BlSliderMv, BlAnnouncementMv} from '@bukalapak/bazaar-mweb-latest';
import {IcoEticket} from '@bukalapak/bazaar-visual-next';

import BestCourses from '../components/LandingPage/BestCourses.vue';
import PageArticle from '../components/LandingPage/PageArticle.vue';
import CategoryAndMerchantTab from '../components/LandingPage/CategoryAndMerchantTab.vue';

import FilterSortTab from '../components/SearchPage/FilterSortTab.vue';

import trackerGenerator from '../mixins/tracker-generator';
import {MAIN_BANNER_CATEGORY} from '../config/app';

import {TRACKER_EVENT_VP_EDU_KARIER_BANNER_CLICK, TRACKER_EVENT_VP_EDU_KARIER_VISIT_PAGE_VIEW} from '../config/tracker-events';
import cloneDeep from 'lodash/cloneDeep';

import Braze from 'shared_js/helpers/braze';
import nativeHandler from 'src/shared/mixins/native-handler';
const DynamicSection = () =>
  import(/* webpackPrefetch: true */ /* webpackChunkName: "DynamicSection" */ '../components/shared/DynamicSection.vue');

const MerchantRegistrationBanner = () =>
  import(
    /* webpackPrefetch: true */ /* webpackChunkName: "MerchantRegistrationBanner" */ '../components/LandingPage/MerchantRegistrationBanner.vue'
  );

const defaultBannerImpression = {
  activeIndex: 1,
  impressionTrackedIds: [],
};

export default {
  name: 'LandingPage',
  components: {
    BlNavBarSearchMv,
    BlSliderMv,
    BlAnnouncementMv,
    IcoEticket,
    BestCourses,
    CategoryAndMerchantTab,
    DynamicSection,
    MerchantRegistrationBanner,
    PageArticle,
    FilterSortTab,
  },
  mixins: [trackerGenerator, nativeHandler],
  data() {
    return {
      MAIN_BANNER_CATEGORY,
      banners: {
        [MAIN_BANNER_CATEGORY]: {
          ...cloneDeep(defaultBannerImpression),
          clickTrackerName: TRACKER_EVENT_VP_EDU_KARIER_BANNER_CLICK,
        },
      },
      keywords: '',
      trackedAnnouncements: [],
    };
  },
  computed: {
    ...mapGetters('banners', ['getBanners']),
    ...mapGetters('announcements', ['getAnnouncements']),
    ...mapGetters(['getPreviousPage']),
    isLoggedIn() {
      return !!window.USER?.id;
    },
    fullScreenWidth() {
      let width = window.innerWidth;
      if (width > 480) width = 480;

      return width;
    },
  },
  beforeMount() {
    this.initilize();
  },
  mounted() {
    Braze.sendEvent('edu_karier_homepage_visit');
  },
  methods: {
    initilize() {
      this.$store.commit('courses/resetCoursesFilter');
      this.resetCourses();
      this.initBanners();
      this.initAnnouncements();
      this.trackLandingPage();
    },
    async initBanners() {
      this.onBannerChange(1, {category: MAIN_BANNER_CATEGORY});
    },
    async initAnnouncements() {
      await this.$store.dispatch('announcements/fetchAnnouncements');
    },
    resetCourses() {
      if (['search', 'merchant'].includes(this.getPreviousPage?.name)) {
        this.$store.commit('courses/resetCourses');
        this.$store.commit('categories/setSelectedCategory', null);
      }
    },
    // This method is used for close webview then go back to BL apps
    onCloseWebview() {
      this.closeNative(() => {
        window.history.back();
      });
    },
    handleSearch(value) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }

      this.searchTimeout = setTimeout(() => {
        const lowercasedKeyword = value.toLowerCase();
        this.$store.commit('courses/resetCoursesFilter');
        this.$store.commit('courses/resetCourses');
        this.$store.commit('categories/setSelectedCategory', 'all');
        this.$store.commit('courses/setCoursesFilter', {keywords: lowercasedKeyword});
        this.$router.push({name: 'search', query: {...this.$route.query, keyword: lowercasedKeyword}});
      }, 800);
    },

    async goToTransaksiPage() {
      this.$router.push({name: 'transaksi'});
    },
    onBannerChange(currentActiveBannerIndex, {category = MAIN_BANNER_CATEGORY}) {
      let banners = this.getBanners[category];

      let updatedActiveIndex = (currentActiveBannerIndex - 1) % banners.length;
      if (updatedActiveIndex < 0) updatedActiveIndex = banners.length + updatedActiveIndex;

      this.banners[category].activeIndex = updatedActiveIndex + 1;
    },
    trackBannerOnClick(banner, {category = MAIN_BANNER_CATEGORY}) {
      let trackerName = this.banners[category].clickTrackerName;
      const trackerPayload = {
        banner_name: banner.title,
        banner_url: banner.url,
        banner_position: banner.order.toString(),
      };

      this.mixinSendTracker(trackerName, trackerPayload);
    },
    trackLandingPage() {
      this.mixinSendTracker(TRACKER_EVENT_VP_EDU_KARIER_VISIT_PAGE_VIEW);
    },
  },
};
</script>

<style lang="scss" scoped>
.edu-karier-main-banner-wrapper {
  .bl-slider::v-deep img {
    width: 100% !important;
    object-fit: cover !important;
  }
}
</style>
